import { Ionicons } from '@expo/vector-icons';
import { T, useT } from '@transifex/react';
import { Divider, Heading, HStack, Text, VStack } from '@vidahealth/vida-ui';
import { useEffect, useState } from 'react';
import { Image, Linking, Platform, View } from 'react-native';

import avatar1 from '@/assets/images/onboarding/member-avatar-1.png';
import avatar2 from '@/assets/images/onboarding/member-avatar-2.png';
import avatar3 from '@/assets/images/onboarding/member-avatar-3.png';
import avatar4 from '@/assets/images/onboarding/member-avatar-4.png';
import qrCode from '@/assets/images/onboarding/qr-code.png';
import { trackEvent } from '@/modules/analytics';
import { sendMobileLink } from '@/modules/api';
import { isMobileWeb } from '@/modules/clientContext';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, PhoneField, isValidPhoneDisplay } from '@/modules/ui';

import { resolveDownloadUrl } from '../utils/openAppWithLoginToken';

const MEMBER_AVATARS = [avatar1, avatar2, avatar3, avatar4];
const NAVY = '#012D6A';
// Universal app-download link (Sparta's VIDA_APP_DOWNLOAD) — the page redirects
// to the right store. The native CTA's target, and mobile web's fallback when
// the sesame mint fails.
const VIDA_APP_DOWNLOAD_URL = 'https://vida.com/smsdownload/';

function MemberProof() {
  return (
    <HStack space='lg' className='items-center'>
      <HStack className='items-center'>
        {MEMBER_AVATARS.map((src, i) => (
          <View
            key={i}
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              borderWidth: 2,
              borderColor: '#fff',
              marginLeft: i === 0 ? 0 : -10,
              overflow: 'hidden',
            }}
          >
            <Image source={src} style={{ width: '100%', height: '100%' }} resizeMode='cover' />
          </View>
        ))}
      </HStack>
      <VStack>
        <HStack style={{ alignItems: 'center', gap: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Ionicons key={i} name='star' size={14} color='#AB3192' />
          ))}
          <Ionicons name='star-half' size={14} color='#AB3192' />
        </HStack>
        <Text size='sm' className='font-body text-typography-600'>
          <T _str='Join 300k + Vida members' />
        </Text>
      </VStack>
    </HStack>
  );
}

/**
 * Private to TransitionToNativeAppScreen — the left content panel: download
 * heading, member social proof, and (desktop web) a QR card + "text link"
 * input, or (native / mobile web) a single Download CTA. The text-link button
 * POSTs the number to the SMS service (sendMobileLink) and surfaces a sent /
 * error state.
 *
 * The QR card and "text a link to yourself" row are desktop affordances — on a
 * phone browser the user is already holding the device, so mobile web takes the
 * same single CTA as native, carrying a one-time sesame so the app opens signed
 * in (see `resolveDownloadUrl`).
 */
export function DownloadPanel() {
  const t = useT();
  const { user } = useRulesEngine();
  // `phone` holds the formatted `(xxx) xxx-xxxx` value (PhoneField masks input).
  const [phone, setPhone] = useState('');
  // Inline message under the input — used for BOTH the validation error and a
  // failed send (Sparta surfaces both as the field's helperText). null = none.
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  // Busy state for the Download CTA — mobile web mints a sesame before it can
  // navigate, so the press is async and the button needs a spinner.
  const [isResolvingDownload, setIsResolvingDownload] = useState(false);
  const isNative = Platform.OS === 'ios' || Platform.OS === 'android';
  const onMobileWeb = isMobileWeb();
  // Native builds and phone browsers both get the single Download CTA.
  const showDownloadCta = isNative || onMobileWeb;

  // Text the app-download link to the entered number (Sparta's sendMobileLink).
  // The button stays enabled: an empty / incomplete number surfaces the inline
  // validation message instead of POSTing (mirrors Sparta's stylos
  // DownloadVidaApp Yup gate), and a failed send drops the same inline error
  // under the input while the button returns to its idle state.
  const onTextLink = async () => {
    if (phone === '' || !isValidPhoneDisplay(phone)) {
      setPhoneError(t('Please enter a valid phone number'));
      return;
    }
    trackEvent('transition-click-send-link', {
      uuid: user?.uuid,
      // Desktop web by construction: this CTA lives in the `!showDownloadCta`
      // branch, which mobile web never renders — it takes the Download CTA
      // instead. If that gating changes, this literal has to change with it.
      platform: 'desktop_web',
    });
    setPhoneError(null);
    setStatus('sending');
    try {
      await sendMobileLink(phone);
      setStatus('sent');
      trackEvent('transition-send-link-success', { uuid: user?.uuid });
    } catch {
      setStatus('idle');
      setPhoneError(t('Something went wrong. Please try again.'));
    }
  };

  // Send the user to the app. Native is already signed in, so it opens the plain
  // download page; mobile web mints a one-time sesame first and hands it to the
  // Branch deeplink, which signs the app in on open (or after install, via the
  // deferred deep link). A failed mint degrades to the same plain download page.
  const onDownload = async () => {
    trackEvent('transition-click-download-app', {
      uuid: user?.uuid,
      platform: isNative ? 'native' : 'mobile_web',
    });
    if (isNative) {
      void Linking.openURL(VIDA_APP_DOWNLOAD_URL);
      return;
    }
    setIsResolvingDownload(true);
    const url = await resolveDownloadUrl(VIDA_APP_DOWNLOAD_URL);
    // `location.assign`, not `Linking.openURL` — RNW's openURL is `window.open`,
    // which Safari blocks once the user gesture is consumed by the await above.
    // assign() also leaves the transition screen in history, so a store bounce
    // that doesn't take has somewhere to come back to. The busy flag is cleared
    // by the effect below rather than here, so the button doesn't flash idle
    // during the navigation.
    window.location.assign(url);
  };

  // --- Effects cluster ---
  // Coming BACK to this screen has to clear the busy flag, or the CTA returns
  // spinning and disabled with no way to retry. Two ways back, and neither runs
  // the handler again:
  //   - `pageshow` — the page was restored from bfcache (iOS Safari does this
  //     when the user backs out of the store).
  //   - `visibilitychange` — the deeplink opened the app, so the tab went hidden
  //     without unloading; this fires when the user switches back to the browser.
  // It also covers an assign() that never navigates at all (blocked/cancelled),
  // where no unload happens. On the normal path the tree is gone and neither
  // fires. Registered unconditionally rather than only while busy — a listener
  // added mid-navigation can miss its own restore.
  // `document` is guarded alongside `window`: jest runs this file in a node
  // environment where `window` exists but `document` does not, and an unguarded
  // read throws during render for every consumer of this panel.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (typeof document === 'undefined') return;
    const clearBusy = () => {
      if (document.visibilityState === 'hidden') return;
      setIsResolvingDownload(false);
    };
    window.addEventListener('pageshow', clearBusy);
    document.addEventListener('visibilitychange', clearBusy);
    return () => {
      window.removeEventListener('pageshow', clearBusy);
      document.removeEventListener('visibilitychange', clearBusy);
    };
  }, []);

  return (
    <VStack space='4xl' className='flex-1'>
      <VStack space='sm'>
        <Text size='lg' className='font-body-semibold text-primary-500'>
          <T _str='Download the app' />
        </Text>
        <Heading size='2xl' className='font-heading-semibold text-primary-900 dark:text-primary-0'>
          <T _str='For the best experience, download the mobile app' />
        </Heading>
      </VStack>

      <MemberProof />

      {showDownloadCta ? (
        <CtaButton
          isBusy={isResolvingDownload}
          isDisabled={isResolvingDownload}
          onPress={onDownload}
          testID='transition-download-app'
        >
          <T _str='Continue to mobile experience' />
        </CtaButton>
      ) : (
        <>
          <HStack
            space='lg'
            style={{ backgroundColor: NAVY, borderRadius: 16, padding: 16, alignItems: 'center' }}
          >
            <View className='h-[140px] w-[140px] overflow-hidden rounded-vui-sm bg-white p-0.5'>
              <Image
                source={qrCode}
                style={{ width: '100%', height: '100%' }}
                resizeMode='contain'
                accessibilityLabel={t('Download Vida app QR code')}
              />
            </View>
            <VStack space='xs' className='flex-1'>
              <Text size='md' weight='bold' className='text-typography-0 dark:text-primary-0'>
                <T _str='Scan to download' />
              </Text>
              <Text size='md' className='text-typography-0 dark:text-primary-100'>
                <T _str='Point your phone’s camera at the QR code to get a link' />
              </Text>
            </VStack>
          </HStack>

          <HStack space='md' className='items-center'>
            <View className='flex-1'>
              <Divider />
            </View>
            <Text size='sm' weight='bold' className='text-typography-900'>
              <T _str='OR' />
            </Text>
            <View className='flex-1'>
              <Divider />
            </View>
          </HStack>

          <View className='flex-col gap-4 sm:flex-row sm:items-start'>
            <View className='min-w-[200px] sm:flex-1'>
              <PhoneField
                placeholder={t('Mobile phone number')}
                value={phone}
                onChangeText={(value) => {
                  setPhone(value);
                  setPhoneError(null);
                  setStatus('idle');
                }}
                // Lock the field while the request is in flight — the user can't
                // edit (which would otherwise re-enable the button and allow a
                // duplicate POST), and the row reads as a single busy unit.
                isDisabled={status === 'sending'}
                errorMessage={phoneError ?? undefined}
                testID='transition-phone'
              />
              {/* Success sits in the same slot the field error uses (mutually
                  exclusive — a send clears phoneError), matched to its margins. */}
              {status === 'sent' ? (
                <Text size='sm' className='ml-1 mt-2 text-success-700'>
                  <T _str='Link sent! Check your phone.' />
                </Text>
              ) : null}
            </View>
            <View className='mt-1 sm:flex-1'>
              <CtaButton
                isBusy={status === 'sending'}
                isDisabled={status === 'sending'}
                onPress={onTextLink}
                testID='transition-text-link'
              >
                <T _str='Text link to download' />
              </CtaButton>
            </View>
          </View>
        </>
      )}
    </VStack>
  );
}
